module Meevo.Plugins.PackageTypes {
    "use strict";
    /**
    * Main Section of PackageTypes
    */
    export class MainSection extends Meevo.Components.BaseClasses.DataMaintenanceSectionBase<Model.PackageType> {
        /**
        * Base class override which defines what a DEFAULT model looks like.
        */
        public isEgiftEnabled: boolean;
        public productUnitCriteria = {
            enableProductUsageTracking: true,
            includeNotForSale: true,
            explicitProductInventoryTypes: [
                Meevo.Model.ProductInventoryTypeEnum.ShopSupply,
                Meevo.Model.ProductInventoryTypeEnum.RetailOrShopSupply
            ],
        };
        packageTypeCategoyEnum = Model.PackageTypeCategoryEnum

        customAuditCriteria = {
            nounId: Model.NounEnum.Package,
        };

        constructor(
            sectionId: number,
            displayNameContentKey: string,
            template: string,
            isPrimarySection: boolean,
            handlesOwnCrud: boolean,
            baseEndpoint: string,
            sectionEndpoint: string,
            loggingService: Meevo.Components.Logging.ILoggingService,
            dataService: Meevo.Components.BaseClasses.IDataService,
            protected identityService: Meevo.Components.Common.IIdentityService,
            protected roundingService: Meevo.Components.Common.IRoundingService,
            protected modalService: Meevo.Components.Modal.IModalService,
            noun: Meevo.Model.NounEnum = null,
            appSettings?: Components.Common.IAppSettings,
            sectionSecurityKey?: string
        ) {
            super(sectionId,
                displayNameContentKey,
                template,
                isPrimarySection,
                handlesOwnCrud,
                baseEndpoint,
                sectionEndpoint,
                loggingService,
                dataService,
                identityService,
                noun,
                appSettings,
                sectionSecurityKey);

            this.getEgiftSettings();

        }

        // suppresses error when binding ng-model to a method
        get totalPackageValue(): number {
            return this.getTotalPackageValue();
        }

        createSectionModelInstance(postAdd: (data: any) => any) {
            this.dataService.getTemplate(this.baseEndpoint).then((data) => {
                this.model = data;
                postAdd(data);
            });
        }

        getTotalPackageValue(): number {
            if (!this.model) {
                return 0;
            }

            let totalAmt = [...(this.model.services || []), ...(this.model.products || []), ...(this.model.productUnits || [])]
                .reduce((aggregate, item) => aggregate += (item.fullItemPrice * item.quantity), 0)
            return this.roundingService.normalizeDecimalPlaces(totalAmt);
        }

        hasAnyItems(): boolean {
            return this.hasAnyServices() || _(this.model.products).any() || this.hasAnyProductUnits();
        }

        isPackageNonProductUnitRequired(): boolean {
            if (!this.model) {
                return true;
            }

            const isCentralOffice = this.identityService.current().centralOfficeId === this.model.homeLocationId;

            return ((isCentralOffice && this.model.availableToLocations) || this.identityService.isSingleLocation() || !isCentralOffice) &&
                ((angular.isUndefined(this.model.category) && !this.hasAnyItems()) ||
                (this.model.category === Model.PackageTypeCategoryEnum.Standard && !this.hasAnyServices() && !_(this.model.products).any())
                );
        }

        isPackageProductUnitRequired(): boolean {
            if (!this.model) {
                return true;
            }

            const isCentralOffice = this.identityService.current().centralOfficeId === this.model.homeLocationId;

            return ((isCentralOffice && this.model.availableToLocations) || this.identityService.isSingleLocation() || !isCentralOffice) &&
                ((angular.isUndefined(this.model.category) && !this.hasAnyItems()) ||
                    (this.model.category === Model.PackageTypeCategoryEnum.ProductUsageTracking && !this.hasAnyProductUnits())
                );
        }

        isProductUsageTrackingPackage(): boolean {
            if (!this.model) {
                return false;
            }

            return this.model.category === Model.PackageTypeCategoryEnum.ProductUsageTracking || _(this.model.productUnits).any();
        }

        hasAnyServices(): boolean {
            if (!this.model) {
                return false;
            }

            return _(this.model.services).any();
        }

        hasAnyProductUnits(): boolean {
            if (!this.model) {
                return false;
            }

            return _(this.model.productUnits).any();
        }

        getEgiftSettings(): void {
            this.dataService.getItem(`eGift/settings`).then((eGiftSettings: Model.IEGiftsSettings) => {
                this.isEgiftEnabled = eGiftSettings.settings.enableNewEgiftExperience || eGiftSettings.settings.enablePackagesCalculator;
            });
        }

        preSave() {
            if (!this.hasAnyItems()) {
                this.model.defaultPrepaidTip = 0;
            }
            if (!this.model.isIncludePackageCharge) {
                this.model.packageChargeAmount = 0;
            }
        }

        getProductDisplayName(product) {
            return product.variantDisplayName ?
                `${product.displayName} - ${product.variantDisplayName}` :
                product.displayName;
        }

        isProductUnitSectionVisible(context: PackageTypesContext) {
            return !!this.model && (context.productUsageEnabled || this.model.category === Model.PackageTypeCategoryEnum.ProductUsageTracking)
                && !this.model.isMembershipOnly && !this.model.isAvailableAsEgift && !this.model.isShareable;
        }

        isProductUnitDisabled(context: PackageTypesContext) {
            return this.isDisabledCommon(context) || this.model.products.length > 0 ||
                this.model.services.length > 0 || this.model.category === Model.PackageTypeCategoryEnum.Standard ||
                !context.productUsageEnabled;
        }

        isMembershipOnlyDisabled(context: PackageTypesContext) {
            return this.isDisabledCommon(context) || this.isProductUsageTrackingPackage();
        }

        isShareableDisabled(context: PackageTypesContext) {
            return this.isDisabledCommon(context) || this.model.isMembershipOnly || this.model.isUnlimitedServices ||
                this.isProductUsageTrackingPackage();
        }

        isEGiftDisabled(context: PackageTypesContext) {
            return !this.model || context.isReadonly || !context.isAccessible || this.isProductUsageTrackingPackage();
        }

        isServiceDisabled(context: PackageTypesContext) {
            return this.isDisabledCommon(context) || this.isProductUsageTrackingPackage();
        }

        isProductDisabled(context: PackageTypesContext) {
            return this.isDisabledCommon(context) || this.isProductUsageTrackingPackage();
        }

        isPackageChargeDisabled(context: PackageTypesContext) {
            return this.isDisabledCommon(context) || !context.isAccessible || this.model.isMembershipOnly ||
                (this.model.services.length < 1 && this.model.productUnits.length < 1);
        }

        private isDisabledCommon(context: PackageTypesContext) {
            return !this.model || context.isReadonly || !context.isOwning || !this.model.products || !this.model.services ||
                !this.model.productUnits;
        }

        get isEditMode() {
            const isEditMode = (this.mode == Meevo.Model.MaintenanceFormState.Editing);
            return isEditMode;
        }

        get isCopyMode() {
            const isCopyMode = (this.mode == Meevo.Model.MaintenanceFormState.Copying);
            return isCopyMode;
        }

        onModeChange(newMode: Meevo.Model.MaintenanceFormState, context: PackageTypesContext): void {
            super.onModeChange(newMode, context);
            if (newMode == Meevo.Model.MaintenanceFormState.Editing || newMode == Meevo.Model.MaintenanceFormState.Copying) {
                this.warnAboutRestrictedProductUnits(context);
                this.warnAboutRestrictedServices(context);
            }
        }

        warnAboutRestrictedProductUnits(context: PackageTypesContext) {
            if (!context.productUsageEnabled) {
                return;
            }

            const hasInvalidProductUnits = this.model.productUnits.some(
                productUnit => !this.isProductUnitEligible(productUnit)
            );

            let promise;
            if (hasInvalidProductUnits) {
                promise = this.modalService.alert("MESSAGE_PACKAGE_CONTAINS_INELIGIBLE_PRODUCT_UNIT");
            }
            else {
                promise = Promise.resolve(true);
            }
            return promise;
        }

        isProductUnitEligible(productUnit: Model.PackageTypeProductUnit): boolean {
            const isEnabled = productUnit.enableProductUsageTracking;
            const indexOfType = this.productUnitCriteria.explicitProductInventoryTypes.indexOf(productUnit.productInventoryType);
            const isEligible = (isEnabled && indexOfType >= 0);
            return isEligible;
        }

        hasProductUnitWarning(context: PackageTypesContext, productUnit) {
            return context.productUsageEnabled && !this.isProductUnitEligible(productUnit) &&
                (this.isEditMode || this.isCopyMode)
        }

        preProcessServices(context: PackageTypesContext, response: Array<any>) {
            if (context.productUsageEnabled) {
                const idxlist = [];
                for (let i = 0; i < response.length; ++i) {
                    const item = response[i];
                    if (item.isProductUsageEnabled && item.packageServiceRedemptionEnum == Model.PackageServiceRedemptionEnum.ProductUnit) {
                        idxlist.push(i);
                    }
                }
                for (let i = idxlist.length - 1; i >= 0; --i) {
                    const idx = idxlist[i];
                    response.splice(idx, 1);
                }
            }
        }

        hasServiceWarning(context: PackageTypesContext, service) {
            const redeemAsProductUnit = (service.packageServiceRedemptionEnum == Model.PackageServiceRedemptionEnum.ProductUnit);
            const hasWarning = context.productUsageEnabled
                && service.isProductUsageEnabled
                && redeemAsProductUnit
                && (this.isEditMode || this.isCopyMode);
            return hasWarning;
        }

        warnAboutRestrictedServices(context: PackageTypesContext) {
            if (!context.productUsageEnabled) {
                return;
            }

            const hasInvalidServices = this.model.services.some(service => {
                const redeemAsProductUnit = (service.packageServiceRedemptionEnum == Model.PackageServiceRedemptionEnum.ProductUnit);
                const hasInvalid = (service.isProductUsageEnabled && redeemAsProductUnit);
                return hasInvalid;
            });
            let promise;
            if (hasInvalidServices) {
                promise = this.modalService.alert("MESSAGE_PACKAGE_CONTAINS_INELIGIBLE_SERVICE");
            }
            else {
                promise = Promise.resolve(true);
            }
            return promise;
        }
    }
}